/**
 * useRazorpay Hook
 * Custom React hook for Razorpay payment integration
 */

import { useState, useCallback } from "react";
import {
  createOrder,
  verifyPayment,
  createSubscription,
  verifySubscription,
  cancelSubscription,
  openRazorpayCheckout,
  openRazorpaySubscriptionCheckout,
  CreateOrderRequest,
  CreateOrderResponse,
  VerifyPaymentResponse,
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
  VerifySubscriptionResponse,
} from "../services/api";

interface RazorpayResponse {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

interface RazorpaySubscriptionResponse {
  razorpay_subscription_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

interface UseRazorpayOptions {
  onSuccess?: (response: VerifyPaymentResponse | VerifySubscriptionResponse) => void;
  onError?: (error: Error) => void;
  onDismiss?: () => void;
}

interface UseRazorpayReturn {
  loading: boolean;
  error: string | null;
  orderData: CreateOrderResponse | null;
  initiatePayment: (
    orderRequest: CreateOrderRequest,
    userInfo: { name: string; email: string; phone?: string },
    existingOrder?: CreateOrderResponse
  ) => Promise<void>;
  initiateSubscription: (
    subscriptionRequest: CreateSubscriptionRequest,
    userInfo: { name: string; email: string; phone?: string },
    existingSubscription?: CreateSubscriptionResponse
  ) => Promise<void>;
  clearError: () => void;
}

export const useRazorpay = (options: UseRazorpayOptions = {}): UseRazorpayReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orderData, setOrderData] = useState<CreateOrderResponse | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const initiatePayment = useCallback(
    async (
      orderRequest: CreateOrderRequest,
      userInfo: { name: string; email: string; phone?: string },
      existingOrder?: CreateOrderResponse
    ) => {
      setLoading(true);
      setError(null);

      try {
        // Step 1: Use existing order or create new one
        let order: CreateOrderResponse;
        if (existingOrder) {
          console.log("[Razorpay] Using existing order:", existingOrder);
          order = existingOrder;
        } else {
          console.log("[Razorpay] Creating order:", orderRequest);
          order = await createOrder(orderRequest);
          console.log("[Razorpay] Order created:", order);
        }
        setOrderData(order);

        // Step 2: Open Razorpay checkout modal
        await openRazorpayCheckout(order, userInfo, {
          onSuccess: async (response: RazorpayResponse) => {
            console.log("[Razorpay] Payment successful:", response);
            setLoading(true);

            try {
              // Step 3: Verify payment on backend
              const verification = await verifyPayment({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                tenant_id: orderRequest.tenant_id,
              });

              console.log("[Razorpay] Payment verified:", verification);
              options.onSuccess?.(verification);
            } catch (verifyError) {
              const errorMessage =
                verifyError instanceof Error
                  ? verifyError.message
                  : "Payment verification failed";
              setError(errorMessage);
              options.onError?.(new Error(errorMessage));
            } finally {
              setLoading(false);
            }
          },

          onDismiss: () => {
            console.log("[Razorpay] Payment modal dismissed");
            setLoading(false);
            options.onDismiss?.();
          },

          onError: (err) => {
            const errorMessage =
              err instanceof Error ? err.message : "Payment failed";
            console.error("[Razorpay] Error:", errorMessage);
            setError(errorMessage);
            setLoading(false);
            options.onError?.(new Error(errorMessage));
          },
        });
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to initiate payment";
        console.error("[Razorpay] Order creation error:", errorMessage);
        setError(errorMessage);
        setLoading(false);
        options.onError?.(new Error(errorMessage));
      }
    },
    [options]
  );

  const initiateSubscription = useCallback(
    async (
      subscriptionRequest: CreateSubscriptionRequest,
      userInfo: { name: string; email: string; phone?: string },
      existingSubscription?: CreateSubscriptionResponse
    ) => {
      setLoading(true);
      setError(null);

      try {
        let subscription: CreateSubscriptionResponse;
        if (existingSubscription) {
          console.log("[Razorpay] Using existing subscription:", existingSubscription);
          subscription = existingSubscription;
        } else {
          console.log("[Razorpay] Creating subscription:", subscriptionRequest);
          subscription = await createSubscription(subscriptionRequest);
          console.log("[Razorpay] Subscription created:", subscription);
        }

        await openRazorpaySubscriptionCheckout(subscription, userInfo, {
          onSuccess: async (response: RazorpaySubscriptionResponse) => {
            console.log("[Razorpay] Subscription first charge successful:", response);
            setLoading(true);

            try {
              const verification = await verifySubscription({
                razorpay_subscription_id: response.razorpay_subscription_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                tenant_id: subscriptionRequest.tenant_id,
              });

              console.log("[Razorpay] Subscription verified:", verification);
              options.onSuccess?.(verification);
            } catch (verifyError) {
              const errorMessage =
                verifyError instanceof Error
                  ? verifyError.message
                  : "Subscription verification failed";
              setError(errorMessage);
              options.onError?.(new Error(errorMessage));
            } finally {
              setLoading(false);
            }
          },

          onDismiss: () => {
            console.log("[Razorpay] Subscription modal dismissed");
            // Best-effort: cancel the Subscription Razorpay's API required us
            // to create before it would open this modal. If this call is lost
            // (tab closed, network drop), the mothership's 5-minute sweep
            // still catches it — see cancelSubscription()'s own comment.
            cancelSubscription(subscription.subscription_id);
            setLoading(false);
            options.onDismiss?.();
          },

          onError: (err) => {
            const errorMessage =
              err instanceof Error ? err.message : "Payment failed";
            console.error("[Razorpay] Error:", errorMessage);
            setError(errorMessage);
            setLoading(false);
            options.onError?.(new Error(errorMessage));
          },
        });
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to initiate subscription";
        console.error("[Razorpay] Subscription creation error:", errorMessage);
        setError(errorMessage);
        setLoading(false);
        options.onError?.(new Error(errorMessage));
      }
    },
    [options]
  );

  return {
    loading,
    error,
    orderData,
    initiatePayment,
    initiateSubscription,
    clearError,
  };
};

export default useRazorpay;
